import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { gbp, toDisplay, toGbp, curSymbol } from "@/lib/format";
import AlertCard from "@/components/AlertCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Pencil, Globe, Gauge, Bell, Wallet, Loader2, RefreshCw, BellRing, Ban, Mail, Sparkles, CalendarClock } from "lucide-react";

export default function BudgetSettings({ onBack, onChanged }) {
  const [data, setData] = useState({ thresholds: [], budget_categories: [], month_total: 0 });
  const [alerts, setAlerts] = useState({ alerts: [], unread: 0 });
  const [exclusions, setExclusions] = useState({ exclusions: [], merchants: [] });
  const [suggestions, setSuggestions] = useState({});
  const [templates, setTemplates] = useState({ templates: [], has_data: false });
  const [applying, setApplying] = useState(null);
  const [digest, setDigest] = useState({ enabled: true, day: 0, hour: 8 });
  const [savingDigest, setSavingDigest] = useState(false);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: "", limit: "", rollover: false, warn_percent: 80, period: "monthly" });
  const [saving, setSaving] = useState(false);
  const [newExcl, setNewExcl] = useState("");
  const [digesting, setDigesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, a, e, s, dg] = await Promise.all([
        api.get("/spend-alerts/thresholds"),
        api.get("/spend-alerts"),
        api.get("/spend-alerts/exclusions"),
        api.get("/spend-alerts/suggestions"),
        api.get("/spend-alerts/digest/settings"),
      ]);
      setData(t.data);
      setAlerts(a.data);
      setExclusions(e.data);
      setSuggestions(s.data.suggestions || {});
      setDigest(dg.data);
      api.get("/spend-alerts/templates").then((r) => setTemplates(r.data)).catch(() => {});
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = (preset) => { setEditing(null); setForm({ category: preset || "", limit: "", rollover: false, warn_percent: 80, period: "monthly" }); setModalOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ category: t.category, limit: String(toDisplay(t.limit)), rollover: !!t.rollover, warn_percent: t.warn_percent || 80, period: t.period || "monthly" }); setModalOpen(true); };

  const save = async () => {
    if (!form.category || !form.limit) return;
    setSaving(true);
    try {
      await api.post("/spend-alerts/thresholds", {
        category: form.category,
        limit: toGbp(form.limit),
        rollover: form.rollover,
        warn_percent: parseInt(form.warn_percent, 10) || 80,
        period: form.period,
      });
      toast.success(`${editing ? "Updated" : "Set"} ${form.category === "Overall" ? "overall monthly" : form.category} limit`);
      setModalOpen(false);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    try {
      await api.delete(`/spend-alerts/thresholds/${t.id}`);
      toast.success(`Removed ${t.category} limit`);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const markRead = async (id) => {
    try { await api.post(`/spend-alerts/${id}/read`); await load(); onChanged?.(); } catch {}
  };

  const addExclusion = async () => {
    if (!newExcl) return;
    try {
      await api.post("/spend-alerts/exclusions", { merchant: newExcl });
      toast.success(`Excluded ${newExcl} from budgets`);
      setNewExcl("");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeExclusion = async (id, merchant) => {
    try {
      await api.delete(`/spend-alerts/exclusions/${id}`);
      toast.success(`${merchant} counts toward budgets again`);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendDigest = async () => {
    setDigesting(true);
    try {
      await api.post("/spend-alerts/digest/send");
      toast.success("Digest sent to your inbox");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDigesting(false);
    }
  };

  const applyTemplate = async (key) => {
    setApplying(key);
    try {
      const { data } = await api.post("/spend-alerts/templates/apply", { key });
      toast.success(`Applied ${key} budget — ${data.applied} limits set`);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setApplying(null);
    }
  };

  const saveDigest = async (next) => {
    const merged = { ...digest, ...next };
    setDigest(merged);
    setSavingDigest(true);
    try {
      await api.patch("/spend-alerts/digest/settings", merged);
      toast.success("Digest schedule updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingDigest(false);
    }
  };

  const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const suggestionFor = (cat, period) => {
    const s = suggestions[cat];
    if (!s) return 0;
    return period === "weekly" ? s.weekly : s.monthly;
  };

  const overall = data.thresholds.find((t) => t.category === "Overall");
  const categoryLimits = data.thresholds.filter((t) => t.category !== "Overall");
  const usedCategories = data.thresholds.map((t) => t.category);
  const available = (data.budget_categories || []).filter((c) => c === form.category || !usedCategories.includes(c));
  const excludedNames = exclusions.exclusions.map((e) => e.merchant.toLowerCase());
  const merchantOptions = (exclusions.merchants || []).filter((m) => !excludedNames.includes(m.toLowerCase()));

  const ProgressBar = ({ spent, limitVal, warn }) => {
    const pct = Math.min(100, (spent / limitVal) * 100);
    const over = spent > limitVal;
    const near = !over && pct >= (warn || 80);
    const color = over ? "#f43f5e" : near ? "#f59e0b" : "#00C896";
    return (
      <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    );
  };

  const RolloverBadge = ({ t }) =>
    t.rollover ? (
      <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-[#00C896]/30 bg-[#00C896]/10 text-[#4DE3BF] inline-flex items-center gap-1">
        <RefreshCw className="h-2.5 w-2.5" /> {t.rollover_amount > 0 ? `+${gbp(t.rollover_amount)} rolled over` : "Rollover on"}
      </span>
    ) : null;

  return (
    <div className="mm-fade-up">
      <button data-testid="budget-back-button" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition mb-5">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#00C896]">Budget settings</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Manage your spending limits</h1>
          <p className="text-slate-400 text-sm mt-2 max-w-xl">Set an overall or per-category limit, get an early warning before you go over, roll unspent budget into next month, and exclude merchants you don't want counted.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="send-digest-button" onClick={sendDigest} disabled={digesting} variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5">
            {digesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1.5" /> Email me a digest</>}
          </Button>
          <Button data-testid="budget-add-button" onClick={() => openAdd()} className="rounded-full bg-[#00C896] hover:bg-[#00C896] text-[#04140D] font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> Add limit
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#00C896]" /></div>
      ) : (
        <div className="mt-6 grid grid-cols-12 gap-4 sm:gap-5">
          {templates.has_data && templates.templates.length > 0 && (
            <div className="col-span-12 mm-glow-card p-6" data-testid="templates-card">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-[#00C896]" />
                <h3 className="font-display text-lg font-semibold">Starter budgets</h3>
                <span className="text-xs text-slate-500">built from your last 3 months</span>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {templates.templates.map((tpl) => (
                  <div key={tpl.key} data-testid={`template-${tpl.key}`} className="rounded-xl border border-white/8 bg-[#0D0D0D] p-4 flex flex-col">
                    <div className="flex items-baseline justify-between">
                      <h4 className="font-display font-semibold">{tpl.name}</h4>
                      <span className="font-num text-sm text-[#00C896]">{gbp(tpl.overall)}<span className="text-[#5F6C7D]">/mo</span></span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{tpl.tagline}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {Object.entries(tpl.limits).slice(0, 5).map(([c, v]) => (
                        <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/10 bg-white/[0.04] text-slate-300 font-num">{c} {gbp(v)}</span>
                      ))}
                    </div>
                    <Button
                      data-testid={`apply-template-${tpl.key}`}
                      onClick={() => applyTemplate(tpl.key)}
                      disabled={applying === tpl.key}
                      className="mt-4 w-full rounded-xl bg-[#00C896] hover:bg-[#00E5AC] text-[#04140D] font-semibold"
                    >
                      {applying === tpl.key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="col-span-12 lg:col-span-7 space-y-4">
            {/* Overall */}
            <div className="mm-glow-card p-6" data-testid="overall-budget-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="h-10 w-10 rounded-xl bg-sky-500/15 flex items-center justify-center text-sky-300"><Globe className="h-5 w-5" /></span>
                  <div>
                    <h3 className="font-display text-lg font-semibold">Overall monthly limit</h3>
                    <p className="text-xs text-slate-500">Your total spending cap across every category</p>
                  </div>
                </div>
                {overall && (
                  <div className="flex items-center gap-2">
                    <button data-testid="edit-overall" onClick={() => openEdit(overall)} className="text-slate-400 hover:text-white transition"><Pencil className="h-4 w-4" /></button>
                    <button data-testid="delete-overall" onClick={() => remove(overall)} className="text-slate-400 hover:text-rose-400 transition"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>

              {overall ? (
                <div className="mt-5">
                  <div className="flex items-end justify-between mb-2">
                    <span className="font-num text-2xl font-bold">{gbp(overall.spent)}</span>
                    <span className="font-num text-sm text-slate-400">of {gbp(overall.effective_limit)} <span className="text-[#5F6C7D]">/{overall.period === "weekly" ? "wk" : "mo"}</span></span>
                  </div>
                  <ProgressBar spent={overall.spent} limitVal={overall.effective_limit} warn={overall.warn_percent} />
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-num ${overall.spent > overall.effective_limit ? "text-rose-400" : "text-[#00C896]"}`}>
                      {overall.spent > overall.effective_limit
                        ? `${gbp(overall.spent - overall.effective_limit)} over budget`
                        : `${gbp(overall.effective_limit - overall.spent)} remaining`}
                    </p>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300 inline-flex items-center gap-1"><BellRing className="h-2.5 w-2.5" /> Warns at {overall.warn_percent}%</span>
                    <RolloverBadge t={overall} />
                  </div>
                </div>
              ) : (
                <div className="mt-5 rounded-xl border border-dashed border-white/12 p-6 text-center">
                  <Wallet className="h-6 w-6 text-slate-500 mx-auto" />
                  <p className="text-sm text-slate-400 mt-2">No overall limit yet. You've spent <span className="font-num text-white">{gbp(data.month_total)}</span> this month.</p>
                  <Button data-testid="set-overall-button" onClick={() => openAdd("Overall")} variant="outline" className="mt-3 rounded-full border-[#00C896]/40 bg-transparent text-[#4DE3BF] hover:bg-[#00C896]/10">
                    Set overall limit
                  </Button>
                </div>
              )}
            </div>

            {/* Category limits */}
            <div className="mm-glow-card p-6" data-testid="category-budgets-card">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Gauge className="h-5 w-5 text-slate-400" /> Category limits</h3>
              <div className="mt-4 space-y-3">
                {categoryLimits.map((t) => (
                  <div key={t.id} data-testid={`budget-row-${t.category}`} className="rounded-xl border border-white/8 bg-[#0D0D0D] p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{t.category}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/10 bg-white/[0.04] text-slate-400">{t.period === "weekly" ? "Weekly" : "Monthly"}</span>
                        {t.spent > t.effective_limit && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-rose-500/30 bg-rose-500/10 text-rose-300">Over</span>}
                        <RolloverBadge t={t} />
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-num text-xs text-slate-400">{gbp(t.spent)} / {gbp(t.effective_limit)}</span>
                        <button data-testid={`edit-${t.category}`} onClick={() => openEdit(t)} className="text-slate-500 hover:text-white transition"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`delete-${t.category}`} onClick={() => remove(t)} className="text-slate-500 hover:text-rose-400 transition"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                    <ProgressBar spent={t.spent} limitVal={t.effective_limit} warn={t.warn_percent} />
                  </div>
                ))}
                {categoryLimits.length === 0 && <p className="text-sm text-slate-500">No category limits yet. Add one to get category-level alerts.</p>}
              </div>
            </div>

            {/* Excluded merchants */}
            <div className="mm-glow-card p-6" data-testid="exclusions-card">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Ban className="h-5 w-5 text-slate-400" /> Excluded merchants</h3>
              <p className="text-xs text-slate-500 mt-1">Transactions from these merchants won't count toward any budget. Refunds never count.</p>

              <div className="mt-4 flex items-center gap-2">
                <Select value={newExcl} onValueChange={setNewExcl}>
                  <SelectTrigger data-testid="exclusion-select" className="bg-[#0D0D0D] border-white/10 text-white h-10">
                    <SelectValue placeholder="Choose a merchant to exclude" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#14181D] border-white/10 text-white max-h-64">
                    {merchantOptions.map((m) => <SelectItem key={m} value={m} className="focus:bg-[#00C896]/20">{m}</SelectItem>)}
                    {merchantOptions.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No more merchants</div>}
                  </SelectContent>
                </Select>
                <Button data-testid="add-exclusion-button" onClick={addExclusion} disabled={!newExcl} className="rounded-xl bg-[#00C896] hover:bg-[#00C896] text-[#04140D] font-semibold h-10 shrink-0">Exclude</Button>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {exclusions.exclusions.map((e) => (
                  <span key={e.id} data-testid={`exclusion-${e.merchant}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[#0D0D0D] pl-3 pr-1.5 py-1 text-xs text-slate-200">
                    {e.merchant}
                    <button data-testid={`remove-exclusion-${e.merchant}`} onClick={() => removeExclusion(e.id, e.merchant)} className="h-4 w-4 rounded-full flex items-center justify-center text-slate-500 hover:text-rose-400">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {exclusions.exclusions.length === 0 && <p className="text-sm text-slate-500">No merchants excluded.</p>}
              </div>
            </div>

            {/* Weekly digest schedule */}
            <div className="mm-glow-card p-6" data-testid="digest-settings-card">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><CalendarClock className="h-5 w-5 text-slate-400" /> Weekly digest</h3>
                <Switch data-testid="digest-enabled-switch" checked={digest.enabled} onCheckedChange={(v) => saveDigest({ enabled: !!v })} className="data-[state=checked]:bg-[#00C896]" />
              </div>
              <p className="text-xs text-slate-500 mt-1">A summary email of every budget vs your spending. Choose when it arrives (UTC).</p>
              <div className={`mt-4 grid grid-cols-2 gap-3 ${digest.enabled ? "" : "opacity-40 pointer-events-none"}`}>
                <div>
                  <Label className="text-slate-300 text-xs">Day</Label>
                  <Select value={String(digest.day)} onValueChange={(v) => saveDigest({ day: parseInt(v, 10) })}>
                    <SelectTrigger data-testid="digest-day-select" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white h-10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#14181D] border-white/10 text-white">
                      {DAYS.map((d, i) => <SelectItem key={d} value={String(i)} className="focus:bg-[#00C896]/20">{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Time</Label>
                  <Select value={String(digest.hour)} onValueChange={(v) => saveDigest({ hour: parseInt(v, 10) })}>
                    <SelectTrigger data-testid="digest-hour-select" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white h-10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#14181D] border-white/10 text-white max-h-64">
                      {Array.from({ length: 24 }).map((_, h) => <SelectItem key={h} value={String(h)} className="focus:bg-[#00C896]/20">{String(h).padStart(2, "0")}:00</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-3">{savingDigest ? "Saving…" : digest.enabled ? `Arrives every ${DAYS[digest.day]} at ${String(digest.hour).padStart(2, "0")}:00 UTC.` : "Digest paused."}</p>
            </div>
          </div>

          {/* Alerts */}
          <div className="col-span-12 lg:col-span-5">
            <div className="mm-glow-card p-6" data-testid="budget-alert-feed">
              <div className="flex items-center gap-2 mb-4">
                <Bell className="h-4 w-4 text-[#00C896]" />
                <h3 className="font-display text-lg font-semibold">Alert history</h3>
                {alerts.unread > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500 text-white font-bold">{alerts.unread} new</span>}
              </div>
              <div className="space-y-2.5 max-h-[720px] overflow-y-auto pr-1">
                {alerts.alerts.map((a) => <AlertCard key={a.id} alert={a} onRead={markRead} />)}
                {alerts.alerts.length === 0 && <p className="text-sm text-slate-500">No alerts yet. When spending nears or crosses a limit, alerts appear here and in your inbox.</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#14181D] border-white/10 text-white" data-testid="budget-modal">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Update limit" : "Add a spending limit"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-slate-300 text-sm">Budget type</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })} disabled={!!editing}>
                <SelectTrigger data-testid="budget-category-select" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white">
                  <SelectValue placeholder="Choose overall or a category" />
                </SelectTrigger>
                <SelectContent className="bg-[#14181D] border-white/10 text-white">
                  {available.map((c) => (
                    <SelectItem key={c} value={c} className="focus:bg-[#00C896]/20">{c === "Overall" ? "Overall monthly limit" : c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Budget window</Label>
              <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
                <SelectTrigger data-testid="budget-period-select" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#14181D] border-white/10 text-white">
                  <SelectItem value="monthly" className="focus:bg-[#00C896]/20">Monthly</SelectItem>
                  <SelectItem value="weekly" className="focus:bg-[#00C896]/20">Weekly (resets every Monday)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-sm">{form.period === "weekly" ? "Weekly" : "Monthly"} limit ({curSymbol()})</Label>
              <Input
                data-testid="budget-limit-input"
                type="number" min="1" step="1"
                value={form.limit} onChange={(e) => setForm({ ...form, limit: e.target.value })}
                placeholder="500"
                className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white focus-visible:ring-[#00C896]"
              />
              {form.category && suggestionFor(form.category, form.period) > 0 && (
                <button
                  type="button"
                  data-testid="use-suggestion-button"
                  onClick={() => setForm({ ...form, limit: String(toDisplay(suggestionFor(form.category, form.period))) })}
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#00C896] hover:text-[#4DE3BF] transition"
                >
                  <Sparkles className="h-3 w-3" /> Suggested {gbp(suggestionFor(form.category, form.period))} based on your last 3 months — tap to use
                </button>
              )}
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Early warning at (% of limit)</Label>
              <Input
                data-testid="budget-warn-input"
                type="number" min="50" max="99" step="1"
                value={form.warn_percent} onChange={(e) => setForm({ ...form, warn_percent: e.target.value })}
                className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white focus-visible:ring-[#00C896]"
              />
              <p className="text-[11px] text-slate-500 mt-1">We'll alert you when you reach this percentage, before you go over.</p>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-white/8 bg-[#0D0D0D] px-3.5 py-3">
              <div>
                <p className="text-sm font-medium">Roll over unspent budget</p>
                <p className="text-[11px] text-slate-500">Any amount left at month end is added to next month's limit.</p>
              </div>
              <Switch data-testid="budget-rollover-switch" checked={form.rollover} onCheckedChange={(v) => setForm({ ...form, rollover: !!v })} className="data-[state=checked]:bg-[#00C896]" />
            </div>
            <Button data-testid="budget-save-button" onClick={save} disabled={saving || !form.category || !form.limit} className="w-full rounded-xl bg-[#00C896] hover:bg-[#00C896] text-[#04140D] font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Update limit" : "Save limit"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
